import React, { useState, useRef, useEffect, useCallback } from 'react'
import { LazyMotion, domAnimation, m, AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { getCachedSession } from '@/lib/sessionCache'
import { Capacitor } from '@capacitor/core'
import { useNavigate, Link } from 'react-router-dom'
import { SEOHead } from '@/components/ui/SEOHead'
import { SocialButtons } from '@/components/auth/SocialButtons'
import { PasswordStrengthBar } from '@/components/auth/PasswordStrengthBar'
import { Validator } from '@/utils/abacus'

type AuthMode = 'login' | 'register'
type ViewState = 'form' | 'email-sent' | 'forgot-sent'

/** Supabase hata kodlarını Türkçe empatik mesajlara çevir */
function translateError(error: any): string {
  const msg = error?.message?.toLowerCase() || ''
  const code = error?.code || ''

  if (msg.includes('invalid login credentials') || code === 'invalid_credentials')
    return 'E-posta veya şifre hatalı. Tekrar deneyin.'
  if (msg.includes('user already registered') || code === 'user_already_exists')
    return 'Bu e-posta ile zaten bir hesap var. Giriş yapmayı deneyin.'
  if (msg.includes('password') && (msg.includes('weak') || msg.includes('short')))
    return 'Şifreniz çok zayıf. En az 8 karakter kullanın.'
  if (msg.includes('email rate limit') || msg.includes('rate limit'))
    return 'Çok fazla deneme yapıldı. Birkaç dakika bekleyip tekrar deneyin.'
  if (msg.includes('email not confirmed'))
    return 'E-posta adresiniz henüz doğrulanmamış. Gelen kutunuzu kontrol edin.'
  if (msg.includes('invalid') && msg.includes('email'))
    return 'Geçersiz e-posta adresi. Lütfen geçerli bir e-posta girin.'
  if (msg.includes('captcha'))
    return 'Güvenlik doğrulaması başarısız. Sayfayı yenileyip tekrar deneyin.'

  return error?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.'
}

export default function LoginPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<AuthMode>('login')
  const [view, setView] = useState<ViewState>('form')

  // Form state
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Email doğrulama tekrar gönder
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  // Cloudflare Turnstile
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const turnstileRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  // Native platformlarda Turnstile yüklenemez (capacitor:// scheme uyumsuzluğu)
  const isNative = Capacitor.isNativePlatform()
  const TURNSTILE_SITE_KEY = isNative ? '' : (import.meta.env.VITE_TURNSTILE_SITE_KEY || '')

  const renderTurnstile = useCallback(() => {
    if (!turnstileRef.current || !TURNSTILE_SITE_KEY || !(window as any).turnstile) return
    // Önceki widget varsa temizle
    if (widgetIdRef.current) {
      try { (window as any).turnstile.remove(widgetIdRef.current) } catch {}
      widgetIdRef.current = null
    }
    setCaptchaToken(null)
    widgetIdRef.current = (window as any).turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => setCaptchaToken(token),
      'expired-callback': () => setCaptchaToken(null),
      'error-callback': () => setCaptchaToken(null),
      theme: 'light',
      size: 'flexible',
      appearance: 'interaction-only',
    })
  }, [TURNSTILE_SITE_KEY])

  // Turnstile widget'ı mount/mod değiştiğinde render et
  useEffect(() => {
    const timer = setTimeout(renderTurnstile, 300)
    return () => {
      clearTimeout(timer)
      if (widgetIdRef.current) {
        try { (window as any).turnstile.remove(widgetIdRef.current) } catch {}
        widgetIdRef.current = null
      }
    }
  }, [mode, view, renderTurnstile])

  const resetTurnstile = () => {
    setCaptchaToken(null)
    if (widgetIdRef.current && (window as any).turnstile) {
      try { (window as any).turnstile.reset(widgetIdRef.current) } catch {}
    }
  }

  // ⚠️ KRİTİK DÜZELTME: OAuth Local-First mimaride asenkron gerçekleşir.
  // Tarayıcıdan deep link ile döndükten sonra veya App tekrar açıldığında kullanıcının giriş yaptığını yakalamak için.
  useEffect(() => {
    const BOUNCE_GUARD_MS = 3000
    // Mevcut aktif bir oturum varsa Login sayfasını atla ve doğrudan Dashboard'a git
    const checkBounceAndSession = async () => {
      const { session } = await getCachedSession()
      if (session) {
        const lastHomeCheck = sessionStorage.getItem('home_auth_checked')
        if (lastHomeCheck) {
          const elapsed = Date.now() - parseInt(lastHomeCheck, 10)
          if (elapsed < BOUNCE_GUARD_MS) {
            navigate('/dashboard', { replace: true })
            return
          }
        }
        navigate('/dashboard', { replace: true })
      }
    }

    checkBounceAndSession()

    // Deep link sonrasında şarj olan oturumu yakala
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        navigate('/dashboard', { replace: true })
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [navigate])

  const inputClass =
    'w-full h-12 px-4 bg-[#F8F9FA] border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/10 outline-none transition-colors font-medium'

  /** Giriş Yap */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      })
      if (loginError) throw loginError
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(translateError(err))
      resetTurnstile()
    } finally {
      setLoading(false)
    }
  }

  /** Kayıt Ol */
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validasyonlar
    const nameValidation = Validator.fullName(fullName.trim())
    if (!nameValidation.isValid) {
      setError(nameValidation.error)
      return
    }

    const emailValidation = Validator.email(email.trim())
    if (!emailValidation.isValid) {
      setError(emailValidation.error)
      return
    }

    if (password.length < 8) {
      setError('Şifre en az 8 karakter olmalıdır.')
      return
    }

    setLoading(true)

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            full_name: (nameValidation as any).formatted || fullName.trim(),
          },
          captchaToken: captchaToken || undefined,
        },
      })
      if (signUpError) throw signUpError

      // ⚠️ UYARI: Supabase signUp boş identities döndürdüğünde provider bilgisi VERMEZ.
      if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
        setError('Bu e-posta adresi zaten kayıtlı. Lütfen giriş yapın veya şifrenizi sıfırlayın.')
        setMode('login')
        return
      }

      // Email doğrulama ekranına geç
      setView('email-sent')
    } catch (err) {
      setError(translateError(err))
      resetTurnstile()
    } finally {
      setLoading(false)
    }
  }

  /** Email doğrulama tekrar gönder */
  const handleResendEmail = async () => {
    setResendLoading(true)
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
        options: captchaToken ? { captchaToken } : undefined,
      })
      if (error) throw error
      setResendSuccess(true)
      setTimeout(() => setResendSuccess(false), 5000)
    } catch (err) {
      setError(translateError(err))
    } finally {
      setResendLoading(false)
    }
  }

  /** Şifremi Unuttum */
  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Lütfen önce e-posta adresinizi yazın.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: 'https://app.gunumvar.com.tr/reset-password',
        captchaToken: captchaToken || undefined,
      })
      if (resetError) throw resetError
      setView('forgot-sent')
    } catch (err) {
      setError(translateError(err))
      resetTurnstile()
    } finally {
      setLoading(false)
    }
  }

  const containerVariants: any = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.21, 0.47, 0.32, 0.98] } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-white p-4 font-sans relative overflow-hidden">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-premium-lg overflow-hidden border border-[#E8E2D6] relative">
        <SEOHead title="Giriş Yap" description="Günüm Var! hesabınıza giriş yapın veya yeni hesap oluşturun." />
        
        <div className="p-8 relative">
          <LazyMotion features={domAnimation}>
            <div className="flex flex-col items-center mb-6">
              <m.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="relative w-56 h-56"
              >
                <img
                  src="/Gunum-Var-App-Splash-Logo-288-288.png"
                  alt="Günüm Var!"
                  className="object-contain w-full h-full"
                />
              </m.div>
            </div>

            {/* Email Doğrulama / Şifre Sıfırlama */}
            {(view === 'email-sent' || view === 'forgot-sent') ? (
              <m.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="text-center space-y-5"
              >
                <>
                <div className="w-16 h-16 mx-auto bg-emerald-50 rounded-2xl flex items-center justify-center text-3xl">
                  {view === 'forgot-sent' ? '🔑' : '📬'}
                </div>
                <div>
                  <h1 className="text-xl font-extrabold text-[#1e2329] mb-2">
                    {view === 'forgot-sent' ? 'Şifre Sıfırlama Bağlantısı Gönderildi' : 'Doğrulama Bağlantısı Gönderildi'}
                  </h1>
                  <p className="text-[13px] text-gray-500 leading-relaxed">
                    <span className="font-bold text-gray-700">{email}</span> adresine bir {view === 'forgot-sent' ? 'şifre sıfırlama' : 'doğrulama'} bağlantısı gönderdik.
                    Gelen kutunuzu kontrol edin.
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2.5">
                    <svg width="18" height="18" viewBox="0 0 24 24" className="mt-0.5 flex-shrink-0" aria-hidden="true">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <p className="text-[11px] text-blue-700 leading-relaxed font-medium">
                      Google ile kayıt olduysanız şifre sıfırlamaya gerek yoktur.
                      Doğrudan <span className="font-bold">Google ile giriş yap</span> butonunu kullanabilirsiniz.
                    </p>
                  </div>

                  {view === 'email-sent' && (
                    <button
                      onClick={handleResendEmail}
                      disabled={resendLoading || resendSuccess}
                      className="w-full py-3 text-[13px] font-bold text-[#D4AF37] border border-[#D4AF37]/30 rounded-xl hover:bg-[#FDF8E8] transition-colors disabled:opacity-50 active:scale-[0.98]"
                    >
                      {resendLoading ? 'Gönderiliyor…' : resendSuccess ? '✓ Tekrar Gönderildi' : 'E-posta Gelmedi mi? Tekrar Gönder'}
                    </button>
                  )}

                  <button
                    onClick={() => { setView('form'); setMode('login'); setError(null) }}
                    className="w-full py-3 bg-[#001D4C] text-white text-[13px] font-bold rounded-xl shadow-lg hover:bg-[#002b70] transition-colors active:scale-[0.98]"
                  >
                    Giriş Ekranına Dön
                  </button>
                </div>
                </>
              </m.div>
            ) : (
              <>
                {/* ─── Google/Apple ile Devam Et ─── */}
                <SocialButtons emailHint={email} />

                {/* Ayraç */}
                <div className="flex items-center gap-4 w-full my-5">
                  <div className="h-px bg-gray-100 flex-1" />
                  <span className="text-[10px] text-gray-400 font-medium tracking-widest uppercase">
                    veya e-posta ile
                  </span>
                  <div className="h-px bg-gray-100 flex-1" />
                </div>

                <AnimatePresence mode="wait">
                  {mode === 'login' ? (
                    /* ─── LOGIN FORM ─── */
                    <m.div
                      key="login"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="space-y-4"
                    >
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                          <label htmlFor="login-email" className="text-sm font-semibold text-gray-700 ml-1 cursor-pointer">E-posta Adresi</label>
                          <input
                            id="login-email"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="ornek@eposta.com"
                            autoComplete="email"
                            required
                            className={inputClass}
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between items-center px-1">
                            <label htmlFor="login-password" className="text-sm font-semibold text-gray-700 cursor-pointer">Şifre</label>
                            <button type="button" onClick={handleForgotPassword} className="text-xs text-[#D4AF37] font-bold hover:underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] rounded">
                              Şifremi unuttum
                            </button>
                          </div>
                          <div className="relative">
                            <input
                              id="login-password"
                              type={showPassword ? 'text' : 'password'}
                              value={password}
                              onChange={e => setPassword(e.target.value)}
                              autoComplete="current-password"
                              required
                              className={`${inputClass} pr-16`}
                              placeholder="••••••••"
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D4AF37] font-bold text-xs p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] rounded"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? 'Gizle' : 'Göster'}
                            </button>
                          </div>
                        </div>

                        {error && (
                          <p className="text-red-500 text-xs font-bold bg-red-50 p-3 rounded-lg border border-red-100">
                            {error}
                          </p>
                        )}

                        <button
                          type="submit"
                          disabled={loading || !email}
                          className="w-full h-13 bg-[#001D4C] text-white font-black text-base rounded-xl shadow-lg hover:bg-[#002b70] hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 disabled:opacity-30 disabled:scale-100 shadow-blue-900/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001D4C] focus-visible:ring-offset-2"
                        >
                          {loading ? 'Yükleniyor…' : 'Giriş Yap'}
                        </button>
                      </form>
                      
                      <div className="text-center pt-2">
                        <button type="button" onClick={() => { setMode('register'); setError(null) }} className="text-[13px] text-gray-500 font-medium hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 rounded px-2 py-1">
                          Hesabınız yok mu? <span className="font-bold text-[#1e2329]">Hemen Kayıt Olun</span>
                        </button>
                      </div>
                    </m.div>
                  ) : (
                    /* ─── REGISTER FORM ─── */
                    <m.div
                      key="register"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="space-y-4"
                    >
                      <form onSubmit={handleRegister} className="space-y-4">
                        <div className="space-y-2">
                          <label htmlFor="reg-fullname" className="text-sm font-semibold text-gray-700 ml-1 cursor-pointer">Ad Soyad</label>
                          <input
                            id="reg-fullname"
                            type="text"
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                            placeholder="Adınız Soyadınız"
                            autoComplete="name"
                            required
                            className={inputClass}
                          />
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="reg-email" className="text-sm font-semibold text-gray-700 ml-1 cursor-pointer">E-posta Adresi</label>
                          <input
                            id="reg-email"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="ornek@eposta.com"
                            autoComplete="email"
                            required
                            className={inputClass}
                          />
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="reg-password" className="text-sm font-semibold text-gray-700 ml-1 cursor-pointer">Şifre</label>
                          <div className="relative">
                            <input
                              id="reg-password"
                              type={showPassword ? 'text' : 'password'}
                              value={password}
                              onChange={e => setPassword(e.target.value)}
                              autoComplete="new-password"
                              required
                              minLength={8}
                              className={`${inputClass} pr-16`}
                              placeholder="En az 8 karakter"
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D4AF37] font-bold text-xs p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] rounded"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? 'Gizle' : 'Göster'}
                            </button>
                          </div>
                          <PasswordStrengthBar password={password} />
                        </div>

                        {error && (
                          <p className="text-red-500 text-xs font-bold bg-red-50 p-3 rounded-lg border border-red-100">
                            {error}
                          </p>
                        )}

                        <button
                          type="submit"
                          disabled={loading || !email || !fullName || password.length < 8}
                          className="w-full h-13 bg-[#001D4C] text-white font-black text-base rounded-xl shadow-lg hover:bg-[#002b70] hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 disabled:opacity-30 disabled:scale-100 shadow-blue-900/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001D4C] focus-visible:ring-offset-2"
                        >
                          {loading ? 'Kayıt yapılıyor…' : 'Kayıt Ol'}
                        </button>
                      </form>

                      {/* Güven Sinyali & Toggle */}
                      <div className="space-y-3 pt-1">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-[10px] text-gray-400 font-medium tracking-wide">🔒 Verileriniz 256-bit şifreleme ile korunur</span>
                        </div>
                        <div className="text-center">
                          <button type="button" onClick={() => { setMode('login'); setError(null) }} className="text-[13px] text-gray-500 font-medium hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 rounded px-2 py-1">
                            Zaten hesabınız var mı? <span className="font-bold text-[#1e2329]">Giriş Yapın</span>
                          </button>
                        </div>
                      </div>
                    </m.div>
                  )}
                </AnimatePresence>

                {/* Turnstile Widget — Native platformlarda gösterilmez */}
                {view === 'form' && !isNative && (
                  <div ref={turnstileRef} className="flex justify-center mt-3" />
                )}
              </>
            )}

            {/* Footer / Hizmet Koşulları */}
            <div className="mt-8 pt-6 border-t border-gray-50 flex flex-col items-center gap-3">
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-bold text-gray-400">
                <Link to="/hizmet-kosullari" className="hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 rounded px-1">Hizmet Koşulları</Link>
                <span aria-hidden="true">·</span>
                <Link to="/gizlilik" className="hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 rounded px-1">Gizlilik Politikası</Link>
                <span aria-hidden="true">·</span>
                <Link to="/iletisim" className="hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 rounded px-1">Destek & İletişim</Link>
              </div>
              <p className="text-[10px] text-gray-400 font-medium max-w-[280px] text-center leading-relaxed">
                {mode === 'login' ? 'Giriş yaparak' : 'Kayıt olarak'} kurallarımızı kabul etmiş sayılırsınız.
              </p>
            </div>
            
          </LazyMotion>
        </div>
      </div>
    </main>
  )
}
